import { getToken } from './auth';
import { API_URL } from './api';

type LogLevel = 'debug' | 'log' | 'warn' | 'error';

type LogCategory =
  | 'HTTP'
  | 'BUSINESS'
  | 'AUTH'
  | 'UI'
  | 'NAVIGATION'
  | 'VITALS'
  | 'UNHANDLED';

interface ClientLogEvent {
  level: LogLevel;
  category: LogCategory;
  event: string;
  message?: string;
  metadata?: Record<string, unknown>;
  url?: string;
  ts: string;
}

const FLUSH_INTERVAL_MS = 10_000;
const MAX_BATCH = 20;
const MAX_BUFFER = 200;
const DEDUP_TTL_MS = 60_000;

class ClientLogReporter {
  private buffer: ClientLogEvent[] = [];
  private seen = new Map<string, number>();
  private timer: number | null = null;
  private started = false;

  start() {
    if (this.started || typeof window === 'undefined') return;
    this.started = true;

    window.addEventListener('error', (e) => {
      this.push({
        level: 'error',
        category: 'UNHANDLED',
        event: 'window_onerror',
        message: e.message,
        metadata: {
          filename: e.filename,
          lineno: e.lineno,
          colno: e.colno,
          stack: (e.error as Error | undefined)?.stack?.slice(0, 2000),
        },
      });
    });

    window.addEventListener('unhandledrejection', (e) => {
      const reason = e.reason as Error | unknown;
      const asError = reason instanceof Error ? reason : new Error(String(reason));
      this.push({
        level: 'error',
        category: 'UNHANDLED',
        event: 'unhandled_rejection',
        message: asError.message,
        metadata: { stack: asError.stack?.slice(0, 2000) },
      });
    });

    this.timer = window.setInterval(() => this.flush(), FLUSH_INTERVAL_MS);
    window.addEventListener('beforeunload', () => this.flush(true));
  }

  push(partial: Omit<ClientLogEvent, 'ts' | 'url'> & { ts?: string; url?: string }) {
    if (this.buffer.length >= MAX_BUFFER) return;
    const ev: ClientLogEvent = {
      ...partial,
      ts: partial.ts ?? new Date().toISOString(),
      url: partial.url ?? (typeof window !== 'undefined' ? window.location.href : ''),
    };
    const fp = ev.level + '|' + ev.category + '|' + ev.event + '|' + (ev.message ?? '').slice(0, 200);
    const now = Date.now();
    const last = this.seen.get(fp);
    if (last && now - last < DEDUP_TTL_MS) return;
    this.seen.set(fp, now);
    this.buffer.push(ev);
  }

  async flush(sync = false) {
    if (this.buffer.length === 0) return;
    const batch = this.buffer.splice(0, MAX_BATCH);
    const token = getToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const url = `${API_URL}/api/client-logs`;
    const body = JSON.stringify({ events: batch });

    try {
      if (sync && typeof navigator !== 'undefined' && navigator.sendBeacon) {
        const blob = new Blob([body], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
        return;
      }
      await fetch(url, {
        method: 'POST',
        headers,
        body,
        keepalive: true,
      });
    } catch {
      // Re-buffer a few on failure to avoid starvation; drop the rest.
      const keep = batch.slice(0, Math.max(0, MAX_BUFFER - this.buffer.length));
      this.buffer.unshift(...keep);
    }
  }
}

export const clientLogs = new ClientLogReporter();

export function logEvent(
  level: LogLevel,
  category: LogCategory,
  event: string,
  metadata?: Record<string, unknown>,
  message?: string,
) {
  clientLogs.push({ level, category, event, message, metadata });
}
